import type { Value } from "platejs";

/**
 * Guarantee a structurally valid Slate/Plate document.
 *
 * slate-react renders any node that is not a text leaf as an element and then
 * calls `Array.from(node.children)` (slate-dom `splitDecorationsByChild`). A
 * non-text node whose `children` is missing or not an array therefore crashes
 * the whole editor with "undefined is not iterable (cannot read property
 * Symbol(Symbol.iterator))".
 *
 * Legacy macro `htmlContent` is migrated into CRDB verbatim and deserialized
 * with `deserializeHtml`. Exotic markup (Office paste artifacts, tables, empty
 * custom elements, etc.) can yield a node with a `type` but no `children`,
 * which is enough to take down the macro editor on open. This walker repairs
 * the value before it reaches the editor: every non-text node gets a valid
 * `children` array, elements never end up empty, and stray top-level text is
 * wrapped in a paragraph rather than dropped.
 */

interface PlateNode {
  text?: unknown;
  children?: unknown;
  [key: string]: unknown;
}

const EMPTY_PARAGRAPH = (): PlateNode => ({
  type: "p",
  children: [{ text: "" }],
});

const isTextNode = (node: PlateNode): boolean => typeof node.text === "string";

/**
 * Sanitize a list of descendants. Drops entries that are not plain objects,
 * recursively repairs element children, and guarantees the returned list is
 * never empty (Slate requires every element to contain at least one child).
 */
function sanitizeChildren(children: unknown): PlateNode[] {
  const input = Array.isArray(children) ? children : [];
  const output: PlateNode[] = [];

  for (const child of input) {
    if (child == null || typeof child !== "object") {
      continue;
    }
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    const node = child as PlateNode;
    if (isTextNode(node)) {
      output.push(node);
      continue;
    }
    output.push({ ...node, children: sanitizeChildren(node.children) });
  }

  return output.length > 0 ? output : [{ text: "" }];
}

/**
 * Sanitize the top-level editor value. The root may only contain block
 * elements, so any stray text leaf that survives sanitization is wrapped in a
 * paragraph (preserving its content) and an otherwise-empty document falls back
 * to a single empty paragraph.
 */
export function sanitizePlateValue(value: unknown): Value {
  if (!Array.isArray(value)) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    return [EMPTY_PARAGRAPH()] as Value;
  }

  const sanitized = sanitizeChildren(value);

  const root: PlateNode[] = [];
  let pendingText: PlateNode[] | null = null;
  const flushPendingText = () => {
    if (pendingText && pendingText.length > 0) {
      root.push({ type: "p", children: pendingText });
    }
    pendingText = null;
  };

  for (const node of sanitized) {
    if (isTextNode(node)) {
      (pendingText ??= []).push(node);
    } else {
      flushPendingText();
      root.push(node);
    }
  }
  flushPendingText();

  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  return (root.length > 0 ? root : [EMPTY_PARAGRAPH()]) as Value;
}
